import React, { FC, FunctionComponent, useMemo } from 'react'
import { ThemeProvider } from 'next-themes'

export enum ToastType {
    Danger = 'DANGER',
    Success = 'SUCCESS',
    Warning = 'WARNING'
}

type ModalViewData = {
    email?: string
} & {
    [key: string]: string | number
}

export interface State {
    displaySearch: boolean
    displaySidebar: boolean
    displayNotify: boolean
    displayDropdown: boolean
    displayModal: boolean
    displayToast: boolean
    displayCartToast: boolean
    isMinicartLoading: boolean
    modalView: string
    modalViewData: ModalViewData | null
    toastText: string | JSX.Element
    toastType: ToastType
    cartToastText: string
    cartToastType: ToastType
    LoadingIndicator: FunctionComponent
    isFixed?: boolean
    suppressCartQuery?: boolean
    navbarScrolled?: boolean
    cartEditId?: string
}

export interface DispatchActions {
    openSearch: () => void
    closeSearch: () => void
    toggleSearch: () => void
    openSidebar: (suppressCartQuery?: boolean) => void
    closeSidebar: () => void
    toggleSidebar: () => void
    openNotify: (suppressCartQuery?: boolean) => void
    closeNotify: () => void
    setMinicartLoading: (isLoading: boolean) => void
    closeSidebarIfPresent: () => false | void
    openDropdown: () => void
    closeDropdown: () => void
    openModal: () => void
    closeModal: () => void
    setModalView: (view: MODAL_VIEWS, data?: ModalViewData) => void
    openToast: (
        toastText: ToastTextHtml,
        toastType?: ToastType,
        isFixed?: boolean
    ) => void
    openCartToast: (
        toastText: ToastText,
        toastType?: ToastType,
        autoCloseCartToast?: boolean
    ) => void
    closeToast: () => void
    closeCartToast: () => void
    scrollNavbar: (navbarScrolled: boolean) => void
    setcartEditId: (cartEditId?: string) => void
}

const initialState = {
    displaySearch: false,
    displaySidebar: false,
    displayNotify: false,
    displayDropdown: false,
    displayModal: false,
    isMinicartLoading: false,
    modalView: 'LOGIN_VIEW',
    modalViewData: null,
    displayToast: false,
    displayCartToast: false,
    toastText: '',
    toastType: ToastType.Success,
    cartToastText: '',
    cartToastType: ToastType.Success,
    LoadingIndicator: () => null,
    isFixed: false,
    suppressCartQuery: false,
    navbarScrolled: false,
    cartEditId: undefined
}

type Action =
    | {
          type: 'OPEN_SEARCH'
      }
    | {
          type: 'CLOSE_SEARCH'
      }
    | {
          type: 'OPEN_SIDEBAR'
          suppressCartQuery?: boolean
      }
    | {
          type: 'OPEN_NOTIFY'
          suppressCartQuery?: boolean
      }
    | {
          type: 'CLOSE_SIDEBAR'
      }
    | {
          type: 'CLOSE_NOTIFY'
      }
    | {
          type: 'OPEN_TOAST'
          toastText: ToastTextHtml
          toastType: ToastType
          isFixed: boolean
      }
    | {
          type: 'CLOSE_TOAST'
      }
    | {
          type: 'OPEN_CART_TOAST'
          toastText: ToastText
          toastType: ToastType
      }
    | {
          type: 'CLOSE_CART_TOAST'
      }
    | {
          type: 'SET_MINICART_LOADING'
          isLoading: boolean
      }
    | {
          type: 'OPEN_DROPDOWN'
      }
    | {
          type: 'CLOSE_DROPDOWN'
      }
    | {
          type: 'OPEN_MODAL'
      }
    | {
          type: 'CLOSE_MODAL'
      }
    | {
          type: 'SET_MODAL_VIEW'
          view: MODAL_VIEWS
          data?: ModalViewData
      }
    | {
          type: 'SCROLL_NAVBAR'
          navbarScrolled?: boolean
      }
    | {
          type: 'SET_CART_EDIT_ID'
          cartEditId: string
      }

type MODAL_VIEWS = 'SIGNUP_VIEW' | 'LOGIN_VIEW' | 'FORGOT_VIEW'
type ToastText = string
type ToastTextHtml = string | JSX.Element

export const UIContext = React.createContext<State | any>(initialState)

UIContext.displayName = 'UIContext'

function uiReducer(state: State, action: Action) {
    switch (action.type) {
        case 'OPEN_SEARCH': {
            return {
                ...state,
                displaySearch: true
            }
        }
        case 'CLOSE_SEARCH': {
            return {
                ...state,
                displaySearch: false
            }
        }
        case 'OPEN_SIDEBAR': {
            return {
                ...state,
                displaySidebar: true,
                suppressCartQuery: action.suppressCartQuery
            }
        }
        case 'OPEN_NOTIFY': {
            return {
                ...state,
                displayNotify: true,
                suppressCartQuery: action.suppressCartQuery
            }
        }
        case 'CLOSE_SIDEBAR': {
            return {
                ...state,
                displaySidebar: false
            }
        }
        case 'CLOSE_NOTIFY': {
            return {
                ...state,
                displayNotify: false
            }
        }
        case 'OPEN_DROPDOWN': {
            return {
                ...state,
                displayDropdown: true
            }
        }
        case 'CLOSE_DROPDOWN': {
            return {
                ...state,
                displayDropdown: false
            }
        }
        case 'OPEN_MODAL': {
            return {
                ...state,
                displayModal: true,
                displaySidebar: false,
                displayNotify: false
            }
        }
        case 'CLOSE_MODAL': {
            return {
                ...state,
                displayModal: false,
                modalViewData: null
            }
        }
        case 'OPEN_TOAST': {
            return {
                ...state,
                displayToast: true,
                toastText: action.toastText,
                toastType: action.toastType || ToastType.Success,
                isFixed: action.isFixed
            }
        }
        case 'CLOSE_TOAST': {
            return {
                ...state,
                displayToast: false
            }
        }
        case 'OPEN_CART_TOAST': {
            return {
                ...state,
                displayCartToast: true,
                cartToastText: action.toastText,
                cartToastType: action.toastType || ToastType.Success
            }
        }
        case 'CLOSE_CART_TOAST': {
            return {
                ...state,
                displayCartToast: false
            }
        }
        case 'SET_MINICART_LOADING': {
            const { isLoading } = action
            return {
                ...state,
                isMinicartLoading: isLoading
            }
        }
        case 'SET_MODAL_VIEW': {
            const { data } = action
            return {
                ...state,
                modalView: action.view,
                modalViewData: data ?? null
            }
        }
        case 'SCROLL_NAVBAR': {
            const { navbarScrolled } = action
            return {
                ...state,
                navbarScrolled
            }
        }
        case 'SET_CART_EDIT_ID': {
            const { cartEditId } = action
            return {
                ...state,
                cartEditId
            }
        }
    }
}

const DEFAULT_TOAST_TIMEOUT = 3000

export const UIProvider: FC<{
    LoadingIndicator: React.ElementType
    toastTimeout: number
}> = (props) => {
    const [state, dispatch] = React.useReducer(uiReducer, initialState)
    const { toastTimeout } = props
    const openSearch = () => dispatch({ type: 'OPEN_SEARCH' })
    const closeSearch = () => dispatch({ type: 'CLOSE_SEARCH' })
    const toggleSearch = () =>
        state.displaySearch ? closeSearch() : openSearch()

    const openSidebar = (suppressCartQuery = false) =>
        dispatch({ type: 'OPEN_SIDEBAR', suppressCartQuery })
    const closeSidebar = () => dispatch({ type: 'CLOSE_SIDEBAR' })
    const openNotify = (suppressCartQuery = false) =>
        dispatch({ type: 'OPEN_NOTIFY', suppressCartQuery })
    const closeNotify = () => dispatch({ type: 'CLOSE_NOTIFY' })
    const toggleSidebar = () =>
        state.displaySidebar
            ? dispatch({ type: 'CLOSE_SIDEBAR' })
            : dispatch({ type: 'OPEN_SIDEBAR' })
    const closeSidebarIfPresent = () =>
        state.displaySidebar && dispatch({ type: 'CLOSE_SIDEBAR' })

    const openDropdown = () => dispatch({ type: 'OPEN_DROPDOWN' })
    const closeDropdown = () => dispatch({ type: 'CLOSE_DROPDOWN' })

    const openModal = () => dispatch({ type: 'OPEN_MODAL' })
    const closeModal = () => dispatch({ type: 'CLOSE_MODAL' })

    const openToast = (
        toastText: ToastTextHtml,
        toastType = ToastType.Success,
        isFixed = false
    ) => {
        dispatch({ type: 'OPEN_TOAST', toastText, toastType, isFixed })
        setTimeout(closeToast, toastTimeout)
    }
    const closeToast = () => dispatch({ type: 'CLOSE_TOAST' })

    const openCartToast = (
        toastText: ToastText,
        toastType = ToastType.Success,
        autoCloseCartToast = true
    ) => {
        dispatch({ type: 'OPEN_CART_TOAST', toastText, toastType })
        if (autoCloseCartToast) setTimeout(closeCartToast, toastTimeout)
    }
    const closeCartToast = () => dispatch({ type: 'CLOSE_CART_TOAST' })

    const setMinicartLoading = (isLoading: boolean) =>
        dispatch({ type: 'SET_MINICART_LOADING', isLoading })

    const setModalView = (view: MODAL_VIEWS, data?: ModalViewData) =>
        dispatch({ type: 'SET_MODAL_VIEW', view, data })

    const scrollNavbar = (navbarScrolled: boolean) =>
        dispatch({ type: 'SCROLL_NAVBAR', navbarScrolled })

    const setcartEditId = (cartEditId: string) =>
        dispatch({ type: 'SET_CART_EDIT_ID', cartEditId })

    const value = useMemo(
        () => ({
            ...state,
            openSearch,
            closeSearch,
            toggleSearch,
            openSidebar,
            openNotify,
            closeNotify,
            closeSidebar,
            toggleSidebar,
            closeSidebarIfPresent,
            openDropdown,
            closeDropdown,
            openModal,
            closeModal,
            setModalView,
            scrollNavbar,
            openToast,
            closeToast,
            openCartToast,
            closeCartToast,
            setMinicartLoading,
            setcartEditId,
            LoadingIndicator: props.LoadingIndicator
        }),
        // eslint-disable-next-line
        [state]
    )

    return <UIContext.Provider value={value} {...props} />
}

export const useUI = (): State & DispatchActions => {
    const context = React.useContext(UIContext)
    if (context === undefined) {
        throw new Error(`useUI must be used within a UIProvider`)
    }
    return context
}

export const ManagedUIContext: FC<{
    LoadingIndicator: React.ElementType
    toastTimeout?: number
}> = ({ children, LoadingIndicator, toastTimeout = DEFAULT_TOAST_TIMEOUT }) => (
    <UIProvider LoadingIndicator={LoadingIndicator} toastTimeout={toastTimeout}>
        <ThemeProvider>{children}</ThemeProvider>
    </UIProvider>
)
